import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { apiFetch } from "@/integrations/backend/client";
import { toast } from "sonner";

interface VerificationResponse {
  session_id: string;
  subscription: {
    id: string;
    status: string;
    is_active: boolean;
    billing_cycle: string;
    pricing_tier: {
      id: string;
      name: string;
    } | null;
    stripe_subscription_id: string | null;
  };
  payment_status: string | null;
  payment_confirmed: boolean;
  onboarding_completed: boolean;
  transaction: {
    id: string;
    amount: number;
    currency: string;
    status: string;
    paid_at: string | null;
  } | null;
}

export default function PaymentVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { profile, refetchProfile } = useAuth();
  const { refetch: refetchSubscriptionStatus } = useSubscriptionStatus();
  const [loading, setLoading] = useState(true);
  const [verificationData, setVerificationData] = useState<VerificationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const redirectingRef = useRef(false);
  const hasVerifiedRef = useRef(false);

  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) {
      setError("No session ID provided");
      setLoading(false);
      return;
    }

    // Prevent multiple verifications for the same session
    if (hasVerifiedRef.current) {
      return;
    }

    const verifyPayment = async () => {
      // Mark as verified immediately to prevent re-runs
      hasVerifiedRef.current = true;
      
      try {
        const res = await apiFetch(`/api/v1/payments/verify-checkout?session_id=${sessionId}`);
        
        if (!res.ok) {
          const errorData = await res.json().catch(() => ({ detail: "Unknown error" }));
          throw new Error(errorData.detail || "Failed to verify payment");
        }

        const data = await res.json() as VerificationResponse;
        setVerificationData(data);
        
        // Refetch profile to get updated status
        await refetchProfile();
        
        // Refetch subscription status to update subscription check
        await refetchSubscriptionStatus();
        
        // If payment is confirmed, redirect immediately (backend has already set onboarding_completed)
        if (data.payment_confirmed && data.onboarding_completed) {
          const target = "/client-profile?tab=legal";
          // Show success toast only once
          toast.success("Payment verified successfully!");
          
          // Redirect immediately after a short delay to show success message
          setRedirecting(true);
          redirectingRef.current = true;
          setTimeout(() => {
            navigate(target, { replace: true });
          }, 2000);
        } else if (data.payment_confirmed) {
          const target = "/client-profile?tab=legal";
          // Payment confirmed but onboarding not completed yet (shouldn't happen, but handle gracefully)
          toast.success("Payment verified successfully!");
          setRedirecting(true);
          redirectingRef.current = true;
          setTimeout(() => {
            navigate(target, { replace: true });
          }, 2000);
        }
      } catch (err: any) {
        console.error("Payment verification error:", err);
        setError(err.message || "Failed to verify payment");
        toast.error(err.message || "Failed to verify payment");
        // Reset the ref on error so user can retry
        hasVerifiedRef.current = false;
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  if (loading) {
    return (
      <div className="min-h-screen gradient-dark flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-primary/20 shadow-neon-glow bg-dark-card">
          <CardContent className="p-8">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="h-12 w-12 text-primary animate-spin" />
              <p className="text-white text-center">Verifying your payment...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen gradient-dark flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-primary/20 shadow-neon-glow bg-dark-card">
          <CardHeader>
            <CardTitle className="text-white text-center flex items-center justify-center gap-2">
              <XCircle className="h-6 w-6 text-red-500" />
              Payment Verification Failed
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-gray-light text-center">{error}</p>
            <div className="flex flex-col gap-2">
              <Button
                onClick={() => navigate("/onboarding", { replace: true })}
                className="w-full"
              >
                Return to Onboarding
              </Button>
              <Button
                onClick={() => navigate("/client-portal", { replace: true })}
                variant="outline"
                className="w-full"
              >
                Go to Portal
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!verificationData) {
    return null;
  }

  const isSuccess = verificationData.payment_confirmed && 
                    verificationData.onboarding_completed;

  return (
    <div className="min-h-screen gradient-dark flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-primary/20 shadow-neon-glow bg-dark-card">
        <CardHeader>
          <CardTitle className="text-white text-center flex items-center justify-center gap-2">
            {isSuccess ? (
              <>
                <CheckCircle2 className="h-6 w-6 text-green-500" />
                Payment Verified!
              </>
            ) : (
              <>
                <AlertCircle className="h-6 w-6 text-yellow-500" />
                Payment Processing
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {isSuccess ? (
            <>
              <div className="space-y-2">
                <p className="text-white text-center font-medium">
                  Your payment has been successfully processed!
                </p>
                <p className="text-gray-light text-center text-sm">
                  Redirecting you to your dashboard...
                </p>
                {verificationData.subscription.pricing_tier && (
                  <div className="bg-primary/10 p-4 rounded-lg border border-primary/30">
                    <p className="text-white font-semibold text-center">
                      {verificationData.subscription.pricing_tier.name}
                    </p>
                    <p className="text-gray-light text-center text-sm">
                      {verificationData.subscription.billing_cycle === "annual" 
                        ? "Annual Billing" 
                        : "Monthly Billing"}
                    </p>
                  </div>
                )}
                {verificationData.transaction && (
                  <div className="text-center text-sm text-gray-light">
                    <p>Amount: ${verificationData.transaction.amount.toFixed(2)} {verificationData.transaction.currency.toUpperCase()}</p>
                    {verificationData.transaction.paid_at && (
                      <p>Paid on: {new Date(verificationData.transaction.paid_at).toLocaleDateString()}</p>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-white text-center">
                  Your payment is being processed. This may take a few moments.
                </p>
                <p className="text-gray-light text-center text-sm">
                  Status: {verificationData.subscription.status}
                </p>
                <p className="text-gray-light text-center text-xs mt-2">
                  Please wait while we verify your payment...
                </p>
              </div>
              {redirecting && (
                <div className="flex flex-col items-center space-y-2">
                  <Loader2 className="h-6 w-6 text-primary animate-spin" />
                  <p className="text-gray-light text-center text-sm">
                    Preparing your dashboard...
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

